import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { usersProxy } from "./apiGateway";
import type { UserAvatarDeletionHistoryItem } from "./apiGateway";

type HistoryProps = { userId: string; revision: number };
type HistoryState = {
  items: UserAvatarDeletionHistoryItem[];
  revision: number;
  loading: boolean;
  error: string | null;
};

const History = ({ userId, revision }: HistoryProps) => {
  const [expanded, setExpanded] = useState(false);
  const [state, setState] = useState<HistoryState>({
    items: [],
    revision,
    loading: false,
    error: null,
  });
  const mounted = useRef(false);
  const requestSequence = useRef(0);
  const activeRevision = useRef<number | null>(null);
  const loadedRevision = useRef<number | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      activeRevision.current = null;
    };
  }, []);

  const loadHistory = useCallback(async () => {
    if (activeRevision.current === revision) return;
    const token = ++requestSequence.current;
    activeRevision.current = revision;
    setState((prev) => ({ ...prev, revision, loading: true, error: null }));
    try {
      const { items } = await usersProxy.getAvatarDeletionHistory(userId);
      if (!mounted.current || token !== requestSequence.current) return;
      loadedRevision.current = revision;
      setState({ items, revision, loading: false, error: null });
    } catch {
      if (!mounted.current || token !== requestSequence.current) return;
      setState((prev) => ({
        ...prev,
        revision,
        loading: false,
        error: "Не удалось загрузить историю удаления фото",
      }));
    } finally {
      if (token === requestSequence.current) activeRevision.current = null;
    }
  }, [userId, revision]);

  useEffect(() => {
    if (expanded && loadedRevision.current !== revision) void loadHistory();
  }, [expanded, revision, loadHistory]);

  return (
    <Card>
      <CardContent>
        <Box
          component="button"
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? "Свернуть историю удаления фото профиля"
              : "Развернуть историю удаления фото профиля"
          }
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: 0,
            background: "transparent",
            p: 0,
            m: 0,
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          <Typography variant="h6">История удаления фото профиля</Typography>
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </Box>
        {expanded && (
          <Box sx={{ display: "grid", gap: 1.25, mt: 1.5 }}>
            {state.loading || state.revision !== revision ? (
              <Typography color="text.secondary" role="status">
                Загрузка истории...
              </Typography>
            ) : state.error ? (
              <>
                <Alert severity="error">{state.error}</Alert>
                <Button onClick={() => void loadHistory()}>Повторить</Button>
              </>
            ) : state.items.length === 0 ? (
              <Typography color="text.secondary">
                История удалений отсутствует
              </Typography>
            ) : (
              state.items.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 1.25,
                    display: "grid",
                    gap: 0.75,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip label="Удаление фото" size="small" color="error" />
                    <Typography
                      component="time"
                      dateTime={item.deletedAt ?? undefined}
                      variant="body2"
                      color="text.secondary"
                    >
                      {item.deletedAt
                        ? new Date(item.deletedAt).toLocaleString("ru-RU")
                        : "Не указано"}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                    ID фото: {item.avatarId}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    Модератор:{" "}
                    {item.moderator.name ||
                      item.moderator.login ||
                      "Не указано"}{" "}
                    (ID: {item.moderator.id || "Не указано"})
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Remounting by user also discards expansion, cached data and stale requests.
export const UserAvatarDeletionHistory = (props: HistoryProps) => (
  <History key={props.userId} {...props} />
);
